import { AlertaProduccionBanner } from '@/components/dominio/AlertaProduccionBanner';
import { TituloOrdenable } from '@/components/dominio/TituloOrdenable';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMensualObras, useMensualObrasSemanal } from '@/hooks/useAvanceMatriz';
import { ESTADOS_VENTA, colorArgbEstado, colorEstado, colorFondoEstado } from '@/lib/coloresEstado';
import { type FilaExport, exportarMatrizExcel } from '@/lib/exportarExcel';
import { type OrdenColumna, ordenarHermanos, siguienteOrden } from '@/lib/ordenColumna';
import { CaretDown, CaretRight, CircleNotch, DownloadSimple } from '@phosphor-icons/react';
import { type ReactElement, useMemo, useState } from 'react';

const NOMBRES_MES = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
];

type Modo = 'produccion' | 'indirecto';

function fmtMonto(v: number): string {
  if (!v) return '—';
  const neg = v < 0;
  const s = Math.round(Math.abs(v))
    .toLocaleString('es-CR', { maximumFractionDigits: 0 })
    .replace(/,/g, ' ');
  return `${neg ? '-' : ''}₡${s}`;
}

/** Columna a renderizar: mes (semanaIso=null) o semana ISO. */
interface ColDef {
  key: string;
  anio: number;
  mes: number;
  semanaIso: number | null;
}
const keyMesCol = (anio: number, mes: number) => `${anio}-${mes}`;
const keySemCol = (anio: number, mes: number, w: number) => `${anio}-${mes}-w${w}`;

interface Celda {
  valor: number;
  /** Estado de venta de la obra ese mes (solo en celdas de obra, para color). */
  estado?: string | null;
}
interface NodoObra {
  worksNo: string;
  display: string | null;
  porColumna: Map<string, Celda>;
}
interface NodoBloque {
  bloque: string;
  key: string;
  porColumna: Map<string, Celda>;
  obras: NodoObra[];
}
interface NodoProyecto {
  proyecto: string;
  porColumna: Map<string, Celda>;
  bloques: NodoBloque[];
}
function acum(map: Map<string, Celda>, key: string, v: number) {
  const c = map.get(key) ?? { valor: 0 };
  c.valor += v;
  map.set(key, c);
}

export function MensualPantalla() {
  const [modo, setModo] = useState<Modo>('produccion');
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  // El orden se guarda por CLAVE de columna (no por posición): al cambiar de vista
  // mes/semana o de filtros las columnas cambian y un orden huérfano se ignora.
  const [ordenCol, setOrdenCol] = useState<{ key: string; dir: 'desc' | 'asc' } | null>(null);
  // Vista: 'mes' (resumen mensual) o 'semana' (desglose por semana ISO). Toggle.
  const [vista, setVista] = useState<'mes' | 'semana'>('mes');
  // Mostrar color de fondo por estado de venta de cada obra/mes.
  const [colorearEstado, setColorearEstado] = useState(false);
  // Desglosar el Gran Total en sub-filas por estado de venta.
  const [totalPorEstadoAbierto, setTotalPorEstadoAbierto] = useState(false);
  // El tiempo lo controla SOLO el filtro global de Períodos (año/mes/día); la vista
  // semana desglosa esos períodos por semana ISO, sin un selector propio.
  const modoSemanal = vista === 'semana';

  const { data, isLoading, error } = useMensualObras();
  const { data: dataSem, isLoading: cargandoSem } = useMensualObrasSemanal(modoSemanal);

  const matriz = useMemo(() => {
    // Fuente según el modo: semanal (si hay semanas marcadas) o mensual.
    // Las filas que solo traen costo real (sin producción ni indirecto) no son parte de
    // esta matriz: alimentan únicamente el comparativo Producción vs Costo.
    const filas = (modoSemanal ? (dataSem?.data ?? []) : (data?.data ?? [])).filter(
      (f) => f.produccionMes !== 0 || f.indirectoMes !== 0,
    );

    // Columnas: en modo semanal, una por (anio,mes,semanaIso); en mensual, por (anio,mes).
    const colSet = new Map<string, ColDef>();
    for (const f of filas) {
      if (modoSemanal && f.semanaIso != null) {
        const key = keySemCol(f.anio, f.mes, f.semanaIso);
        if (!colSet.has(key))
          colSet.set(key, { key, anio: f.anio, mes: f.mes, semanaIso: f.semanaIso });
      } else {
        const key = keyMesCol(f.anio, f.mes);
        if (!colSet.has(key)) colSet.set(key, { key, anio: f.anio, mes: f.mes, semanaIso: null });
      }
    }
    const cols = [...colSet.values()].sort((a, b) =>
      a.anio !== b.anio
        ? a.anio - b.anio
        : a.mes !== b.mes
          ? a.mes - b.mes
          : (a.semanaIso ?? 0) - (b.semanaIso ?? 0),
    );

    const porObra = new Map<
      string,
      {
        display: string | null;
        proy: string;
        bloque: string;
        val: Map<string, number>;
        estado: Map<string, string | null>; // colKey → estado de venta del mes
      }
    >();
    const valorDe = (prod: number, ind: number) => (modo === 'produccion' ? prod : ind);

    for (const f of filas) {
      let o = porObra.get(f.worksNo);
      if (!o) {
        o = {
          display: f.displayName,
          proy: f.bloque ?? '(sin proyecto)',
          bloque: f.subBloque ?? '(sin bloque)',
          val: new Map(),
          estado: new Map(),
        };
        porObra.set(f.worksNo, o);
      }
      const colKey =
        modoSemanal && f.semanaIso != null
          ? keySemCol(f.anio, f.mes, f.semanaIso)
          : keyMesCol(f.anio, f.mes);
      o.val.set(colKey, valorDe(f.produccionMes, f.indirectoMes));
      o.estado.set(colKey, f.estadoVenta ?? null);
    }

    const proyectos = new Map<string, NodoProyecto>();
    const bloques = new Map<string, NodoBloque>();

    for (const [worksNo, o] of porObra) {
      const nodoO: NodoObra = { worksNo, display: o.display, porColumna: new Map() };
      for (const col of cols) {
        const valor = o.val.get(col.key) ?? 0;
        const estado = o.estado.get(col.key) ?? null;
        // Crea la celda si hay valor O estado (una obra puede tener estado sin
        // producción ese mes, y queremos colorearla igual).
        if (valor !== 0 || estado) nodoO.porColumna.set(col.key, { valor, estado });
      }
      const bloqueKey = `${o.proy}|${o.bloque}`;
      let nodoP = proyectos.get(o.proy);
      if (!nodoP) {
        nodoP = { proyecto: o.proy, porColumna: new Map(), bloques: [] };
        proyectos.set(o.proy, nodoP);
      }
      let nodoB = bloques.get(bloqueKey);
      if (!nodoB) {
        nodoB = { bloque: o.bloque, key: bloqueKey, porColumna: new Map(), obras: [] };
        bloques.set(bloqueKey, nodoB);
        nodoP.bloques.push(nodoB);
      }
      nodoB.obras.push(nodoO);
      for (const [k, c] of nodoO.porColumna) {
        acum(nodoB.porColumna, k, c.valor);
        acum(nodoP.porColumna, k, c.valor);
      }
    }

    const arr = [...proyectos.values()].sort((a, b) => a.proyecto.localeCompare(b.proyecto));
    for (const p of arr) {
      p.bloques.sort((a, b) => a.bloque.localeCompare(b.bloque));
      for (const b of p.bloques) b.obras.sort((x, y) => x.worksNo.localeCompare(y.worksNo));
    }
    const total = new Map<string, Celda>();
    for (const p of arr) for (const [k, c] of p.porColumna) acum(total, k, c.valor);

    // Subtotal por estado de venta: por cada estado, suma el valor de las celdas
    // de OBRA que tenían ese estado ese mes. Solo a nivel obra (los grupos no
    // tienen un estado único). estado → (colKey → suma).
    const totalPorEstado = new Map<string, Map<string, number>>();
    for (const p of arr)
      for (const b of p.bloques)
        for (const o of b.obras)
          for (const [k, c] of o.porColumna) {
            if (!c.estado) continue;
            let m = totalPorEstado.get(c.estado);
            if (!m) {
              m = new Map();
              totalPorEstado.set(c.estado, m);
            }
            m.set(k, (m.get(k) ?? 0) + c.valor);
          }

    const anios = [...new Set(cols.map((c) => c.anio))].sort((a, b) => a - b);
    return { cols, proyectos: arr, total, totalPorEstado, anios };
  }, [data, dataSem, modo, modoSemanal]);

  const toggle = (key: string) =>
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const todasKeys = useMemo(() => {
    const k = new Set<string>();
    for (const p of matriz.proyectos) {
      k.add(`P|${p.proyecto}`);
      for (const b of p.bloques) k.add(`B|${b.key}`);
    }
    return k;
  }, [matriz]);

  if (isLoading || (modoSemanal && cargandoSem)) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" /> Cargando…
      </div>
    );
  }
  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const cols = matriz.cols;
  const idxOrden = ordenCol ? cols.findIndex((c) => c.key === ordenCol.key) : -1;
  const orden: OrdenColumna | null =
    ordenCol && idxOrden >= 0 ? { idx: idxOrden, dir: ordenCol.dir } : null;
  const alOrdenar = (idx: number) => {
    const sig = siguienteOrden(orden, idx);
    const col = sig ? cols[sig.idx] : undefined;
    setOrdenCol(sig && col ? { key: col.key, dir: sig.dir } : null);
  };
  /** Valores de un nodo por columna (el "—" de las celdas vacías va siempre al final). */
  const valoresNodo = (n: { porColumna: Map<string, Celda> }) =>
    cols.map((c) => n.porColumna.get(c.key)?.valor || null);
  const proyectosOrd = ordenarHermanos(matriz.proyectos, orden, valoresNodo);
  const bloquesOrd = (p: NodoProyecto) => ordenarHermanos(p.bloques, orden, valoresNodo);
  const obrasOrd = (b: NodoBloque) => ordenarHermanos(b.obras, orden, valoresNodo);
  const iniciaAnio = (idx: number) => idx === 0 || cols[idx - 1]?.anio !== cols[idx]?.anio;
  // En modo semanal el sub-header de mes agrupa las semanas; en mensual no aplica.
  const etiquetaCol = (c: ColDef) =>
    c.semanaIso != null ? `S${c.semanaIso}` : (NOMBRES_MES[c.mes - 1] ?? String(c.mes));
  // Meses (con su colSpan) para la fila de header que agrupa las semanas en modo
  // semanal. Mantiene el orden de las columnas.
  const mesesHeader = (() => {
    const out: { anio: number; mes: number; cnt: number }[] = [];
    for (const c of cols) {
      const ult = out[out.length - 1];
      if (ult && ult.anio === c.anio && ult.mes === c.mes) ult.cnt++;
      else out.push({ anio: c.anio, mes: c.mes, cnt: 1 });
    }
    return out;
  })();

  // conColor: solo las filas de OBRA pintan el fondo según el estado de venta del
  // mes (los grupos proyecto/bloque/total no tienen un estado único).
  const renderCeldas = (porColumna: Map<string, Celda>, conColor = false) =>
    cols.map((c, idx) => {
      const cel = porColumna.get(c.key);
      const borderLeft = iniciaAnio(idx) ? 'border-l border-zinc-200' : '';
      const bg = conColor && colorearEstado ? colorFondoEstado(cel?.estado) : '';
      return (
        <TableCell
          key={c.key}
          className={`text-right text-xs tabular-nums ${borderLeft}`}
          style={bg ? { backgroundColor: bg } : undefined}
          title={cel?.estado ?? undefined}
        >
          {fmtMonto(cel?.valor ?? 0)}
        </TableCell>
      );
    });

  // Exporta la matriz COMPLETA a Excel (todos los niveles, sin importar qué esté
  // expandido en la vista). Valores crudos para que Excel pueda sumar/graficar.
  const exportar = () => {
    const filasExport: FilaExport[] = [];
    const valoresDe = (porColumna: Map<string, Celda>): Map<string, number | null> => {
      const m = new Map<string, number | null>();
      for (const c of cols) m.set(c.key, porColumna.get(c.key)?.valor ?? null);
      return m;
    };
    // Estado de venta por celda de obra (para pintar como "Color por estado").
    const estadosDe = (porColumna: Map<string, Celda>): Map<string, string | null> => {
      const m = new Map<string, string | null>();
      for (const c of cols) m.set(c.key, porColumna.get(c.key)?.estado ?? null);
      return m;
    };
    for (const p of proyectosOrd) {
      filasExport.push({ nivel: 0, etiqueta: p.proyecto, valores: valoresDe(p.porColumna) });
      for (const b of bloquesOrd(p)) {
        filasExport.push({ nivel: 1, etiqueta: b.bloque, valores: valoresDe(b.porColumna) });
        for (const o of obrasOrd(b)) {
          filasExport.push({
            nivel: 2,
            etiqueta: o.worksNo,
            sublabel: o.display && o.display !== o.worksNo ? o.display : null,
            valores: valoresDe(o.porColumna),
            estados: estadosDe(o.porColumna),
          });
        }
      }
    }
    filasExport.push({ nivel: 3, etiqueta: 'Gran Total', valores: valoresDe(matriz.total) });

    // Subtotales por estado de venta (Disponible/Reservado/Formalizado/Entregado),
    // cada uno con su color sólido.
    for (const est of ESTADOS_VENTA) {
      const porCol = matriz.totalPorEstado.get(est);
      if (!porCol) continue;
      const valores = new Map<string, number | null>();
      for (const c of cols) valores.set(c.key, porCol.get(c.key) ?? null);
      filasExport.push({
        nivel: 4,
        etiqueta: est,
        valores,
        colorFila: colorArgbEstado(est, true),
      });
    }

    const titulo = modo === 'produccion' ? 'Venta Mensual' : 'Indirectos Mensual';
    exportarMatrizExcel({
      nombreArchivo: `${modo === 'produccion' ? 'venta' : 'indirectos'}-mensual`,
      tituloHoja: titulo,
      encabezadoJerarquia: 'Proyecto / Bloque / Obra',
      columnas: cols,
      filas: filasExport,
      formato: 'moneda',
    });
  };

  const filas: ReactElement[] = [];
  for (const p of proyectosOrd) {
    const pKey = `P|${p.proyecto}`;
    const pOpen = expandidos.has(pKey);
    filas.push(
      <TableRow key={pKey} className="bg-emerald-100 font-semibold">
        <TableCell className="sticky left-0 z-10 bg-emerald-100 text-xs">
          <button
            type="button"
            onClick={() => toggle(pKey)}
            className="flex items-center gap-1 hover:underline"
          >
            {pOpen ? (
              <CaretDown className="h-3 w-3" weight="bold" />
            ) : (
              <CaretRight className="h-3 w-3" weight="bold" />
            )}
            {p.proyecto}
          </button>
        </TableCell>
        {renderCeldas(p.porColumna)}
      </TableRow>,
    );
    if (!pOpen) continue;
    for (const b of bloquesOrd(p)) {
      const bKey = `B|${b.key}`;
      const bOpen = expandidos.has(bKey);
      filas.push(
        <TableRow key={bKey} className="bg-emerald-50 font-medium">
          <TableCell className="sticky left-0 z-10 bg-emerald-50 text-xs">
            <button
              type="button"
              onClick={() => toggle(bKey)}
              className="flex items-center gap-1 pl-4 hover:underline"
            >
              {bOpen ? (
                <CaretDown className="h-3 w-3" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3" weight="bold" />
              )}
              {b.bloque}
            </button>
          </TableCell>
          {renderCeldas(b.porColumna)}
        </TableRow>,
      );
      if (!bOpen) continue;
      for (const o of obrasOrd(b)) {
        filas.push(
          <TableRow key={`O|${o.worksNo}`}>
            <TableCell className="sticky left-0 z-10 bg-background text-xs">
              <div className="pl-9">
                <div className="font-medium">{o.worksNo}</div>
                {o.display && o.display !== o.worksNo && (
                  <div className="text-[10px] text-muted-foreground truncate max-w-[200px]">
                    {o.display}
                  </div>
                )}
              </div>
            </TableCell>
            {renderCeldas(o.porColumna, true)}
          </TableRow>,
        );
      }
    }
  }

  return (
    <div className="space-y-4">
      <AlertaProduccionBanner />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {modo === 'produccion' ? 'Venta Mensual (Producción)' : 'Indirectos Mensual'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {modo === 'produccion'
            ? 'Producción real del mes (movimientos BC tipo Venta contra la cuenta de ingreso por servicios de construcción 40-10-000-000-001). No incluye certificaciones ni transitorias.'
            : 'Costo indirecto del mes: gasto administrativo que cada obra aporta a Servicios Centrales (cuenta 70-10-000-000-100). Solo desde nov-2025.'}{' '}
          Agrupado por Proyecto → Bloque → Obra. Usá el selector <strong>Semanas</strong> para ver
          el desglose por semana ISO (de los meses/semanas que marqués).
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            <button
              type="button"
              onClick={() => setModo('produccion')}
              className={`px-2.5 py-1 ${modo === 'produccion' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Producción
            </button>
            <button
              type="button"
              onClick={() => setModo('indirecto')}
              className={`px-2.5 py-1 ${modo === 'indirecto' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Indirectos
            </button>
          </div>
          {/* Toggle Mes / Semana */}
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            <button
              type="button"
              onClick={() => setVista('mes')}
              className={`px-2.5 py-1 ${vista === 'mes' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Mes
            </button>
            <button
              type="button"
              onClick={() => setVista('semana')}
              className={`px-2.5 py-1 ${vista === 'semana' ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
            >
              Semana
            </button>
          </div>
          {/* Checkbox: colorear por estado de venta */}
          <label className="flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={colorearEstado}
              onChange={(e) => setColorearEstado(e.target.checked)}
              className="h-3.5 w-3.5"
            />
            Color por estado
          </label>
          {colorearEstado && (
            <span className="flex items-center gap-2">
              {ESTADOS_VENTA.map((est) => (
                <span key={est} className="flex items-center gap-1">
                  <span
                    className="inline-block h-3 w-3 rounded-sm"
                    style={{ backgroundColor: colorEstado(est) }}
                  />
                  {est}
                </span>
              ))}
            </span>
          )}
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setExpandidos(new Set(todasKeys))}
            className="text-blue-600 hover:underline"
          >
            Expandir todo
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setExpandidos(new Set())}
            className="text-blue-600 hover:underline"
          >
            Colapsar
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={exportar}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50"
          >
            <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
            Exportar a Excel
          </button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table wrapperClassName="max-h-[calc(100vh-220px)]">
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 top-0 z-30 bg-background min-w-[220px]">
                  Proyecto / Bloque / Obra
                </TableHead>
                {matriz.anios.map((anio) => {
                  const cnt = cols.filter((c) => c.anio === anio).length;
                  return (
                    <TableHead
                      key={anio}
                      colSpan={cnt}
                      className="sticky top-0 z-20 bg-background text-center border-l border-zinc-200 text-xs"
                    >
                      {anio}
                    </TableHead>
                  );
                })}
              </TableRow>
              {/* En modo semanal, fila de MES que agrupa sus semanas con colSpan. */}
              {modoSemanal && (
                <TableRow>
                  <TableHead className="sticky left-0 top-[33px] z-30 bg-background" />
                  {mesesHeader.map((m) => (
                    <TableHead
                      key={`${m.anio}-${m.mes}`}
                      colSpan={m.cnt}
                      className="sticky top-[33px] z-20 bg-background text-center text-xs border-l border-zinc-200"
                    >
                      {NOMBRES_MES[m.mes - 1]}
                    </TableHead>
                  ))}
                </TableRow>
              )}
              <TableRow>
                <TableHead
                  className={`sticky left-0 z-30 bg-background ${modoSemanal ? 'top-[66px]' : 'top-[33px]'}`}
                />
                {cols.map((c, idx) => (
                  <TableHead
                    key={c.key}
                    className={`sticky z-20 bg-background text-right text-xs min-w-[80px] ${modoSemanal ? 'top-[66px]' : 'top-[33px]'} ${
                      iniciaAnio(idx) ? 'border-l border-zinc-200' : ''
                    }`}
                    title={
                      c.semanaIso != null
                        ? `${NOMBRES_MES[c.mes - 1]} · Semana ISO ${c.semanaIso}`
                        : NOMBRES_MES[c.mes - 1]
                    }
                  >
                    <TituloOrdenable
                      titulo={etiquetaCol(c)}
                      idx={idx}
                      orden={orden}
                      onOrdenar={alOrdenar}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas}
              <TableRow className="border-t-2 border-zinc-300 bg-zinc-100 font-bold">
                <TableCell className="sticky left-0 z-10 bg-zinc-100 text-xs">
                  <button
                    type="button"
                    onClick={() => setTotalPorEstadoAbierto((v) => !v)}
                    className="flex items-center gap-1 hover:underline"
                    title="Desglosar por estado de venta"
                  >
                    {totalPorEstadoAbierto ? (
                      <CaretDown className="h-3 w-3" weight="bold" />
                    ) : (
                      <CaretRight className="h-3 w-3" weight="bold" />
                    )}
                    Gran Total
                  </button>
                </TableCell>
                {renderCeldas(matriz.total)}
              </TableRow>
              {totalPorEstadoAbierto &&
                ESTADOS_VENTA.map((est) => {
                  const porCol = matriz.totalPorEstado.get(est);
                  const bg = colorFondoEstado(est);
                  return (
                    <TableRow key={`TE|${est}`} className="bg-zinc-50 font-medium">
                      <TableCell className="sticky left-0 z-10 bg-zinc-50 text-xs">
                        <span className="flex items-center gap-1.5 pl-4">
                          <span
                            className="inline-block h-3 w-3 rounded-sm"
                            style={{ backgroundColor: colorEstado(est) }}
                          />
                          {est}
                        </span>
                      </TableCell>
                      {cols.map((c, idx) => {
                        const v = porCol?.get(c.key) ?? 0;
                        const borderLeft = iniciaAnio(idx) ? 'border-l border-zinc-200' : '';
                        return (
                          <TableCell
                            key={c.key}
                            className={`text-right text-xs tabular-nums ${borderLeft}`}
                            style={{ backgroundColor: bg }}
                          >
                            {fmtMonto(v)}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
